import styles from "../../../scss/Form.module.scss";
import useFormErrorHandling from "hooks/useFormErrorHandling";
import { Field } from "components/Shared";
import { SaveIcon } from "components/Layout/MainLayout/Sidebar/icons/SaveIcon";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { Box, Card, CardContent, Button, Typography, Grid, Alert } from "@mui/material";
import { FIELDS, IOperationForm, IOperationFormProps,  initialValues,  operationFormSchema } from "./Form.config";
import { useEffect, useRef, useState } from "react";
import { useGetPointOfSaleByCompanyQuery } from "redux/api/pointOfSaleApi";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Checkbox } from "@mui/material";
import { useAppSelector } from "redux/store";
import { IUserLoggedState } from "redux/features/loggedUserSlice";
import { UserRoles } from "Enums/UserRoles";
import { useGetCountryByCodeQuery } from "redux/api/countryApi";



/**
 * Component to render news form
 */

const IMAGE_HEIGHT = "9rem";

export const Form = (props: IOperationFormProps) => {
  const { formatMessage,locale } = useIntl();

  const { user } = useAppSelector(
      (state: { userLoggedState: IUserLoggedState }) => state.userLoggedState
    );
  const { currentOperation, onSubmit, apiError, companies, documentType, profesionalActivities } = props;
  const [nationality, setNationality] = useState(currentOperation?.nationality || '');

  const [selectedCompany, setSelectedCompany] = useState<string>(currentOperation?.companyId || "");
  //API CALL TO FETCH POINTS OF SALE
  const { data: pointsOfSaleData, isLoading: isLoadingPointsOfSale } = useGetPointOfSaleByCompanyQuery(selectedCompany||skipToken);  
  const { data: countryFound, refetch, isFetching, isSuccess:isCountrySucces, error:countryError } = useGetCountryByCodeQuery(
    { code: currentOperation?.nationality || "", language: locale},
    { skip: !currentOperation?.nationality } // No ejecuta la consulta automáticamente
  );
  //natilaty reactive fetch
  useEffect(() => {
    if (currentOperation?.nationality) {
        refetch();
    }
  }, [currentOperation?.nationality, refetch]);

  useEffect(() => {
    if (isCountrySucces && countryFound) {
      console.log("countryFound",countryFound);
      
        setNationality(countryFound);
    }
  }, [isCountrySucces, countryFound]);

  useEffect(() => {
      if (countryError) {
          setNationality(currentOperation?.nationality || ""); 
      }
  }, [countryError, currentOperation?.nationality]); 

  // Form configuration

  const methods = useForm<IOperationForm>({
    resolver: zodResolver(operationFormSchema),
    defaultValues: initialValues(currentOperation),
  });

  // Handle company change
  
  const watchedCompany = useWatch({
    control: methods.control,
    name: FIELDS.COMPANY.FIELD,
  });
  // Referencia para el valor actual
  const prevCompanyRef = useRef(watchedCompany);
  // Submit handler function

  useEffect(() => {
    // Compara el valor actual con el valor anterior almacenado en la referencia
    if (watchedCompany !== prevCompanyRef.current) {
      setSelectedCompany(typeof watchedCompany === "string" ? watchedCompany : "");
      prevCompanyRef.current = watchedCompany; // Actualiza la referencia
    }
  }, [watchedCompany]);
  
  const onSubmitHandler: SubmitHandler<IOperationForm> = (values: IOperationForm) => {
    if(user?.role!==UserRoles.SUPERADMIN){
      return;
    }
    const nationalityToSend= nationality===countryFound ? currentOperation?.nationality : nationality;
    const updatedValues={
      ...values,
      nationality: nationalityToSend
    }
    console.log("updatedValues",updatedValues);
    
    const formData = new FormData();

    formData.append("data", JSON.stringify(updatedValues));
    onSubmit(formData);
  };

  // Error handler hook

  useFormErrorHandling(methods.setError, apiError);    
  useEffect(() => {
    if (methods.formState.errors.general?.message) {
      toast.error(methods.formState.errors.general?.message);
    }
  }
  , [methods.formState.errors.general?.message]);

  const [purchasePurpose, setPurchasePurpose] = useState(
    ["gift", "self-use", "business"].includes(currentOperation?.purchasePurpose || "")
      ? formatMessage({ id: `operation.purchasePurpose.${currentOperation?.purchasePurpose}` })
      : currentOperation?.purchasePurpose || ""
  );
    
  return (
    <Card>
      <CardContent className={styles.cardContent}>
        <FormProvider {...methods}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.ID.FIELD} 
                  label={formatMessage({ id: "operation.fields.id" })}
                  defaultValue={currentOperation?.id}
                  type="text"
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.AMOUNT.FIELD} 
                  label={formatMessage({ id: "operation.fields.amount" })}
                  defaultValue={currentOperation?.amount}
                  type="number"
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.DateTimePicker
                  control={methods.control}
                  name={FIELDS.OPERATION_DATE.FIELD}
                  label={formatMessage({ id: "operation.fields.operationDate" })}
                  defaultValue={currentOperation?.operationDate}
                  helperText={methods.formState.errors[FIELDS.OPERATION_DATE.FIELD]?.message || ''}
                  multiple={false}
                />
              </Grid>
              
              <Grid item xs={6}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.COMPANY.FIELD}
                  label={formatMessage({ id: "operation.fields.Company" })}
                  defaultValue={currentOperation?.companyId}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={companies}  
                  disabled={true}           
                />
              </Grid>
              {currentOperation?.salesChannel === "ECOMMERCE" ? (
                <Grid item xs={6}>
                  <Field.TextField
                    name={FIELDS.SALES_CHANNEL.FIELD} 
                    label={formatMessage({ id: "operation.fields.salesChannel" })}
                    defaultValue={currentOperation?.salesChannel || ""}
                    type="text"
                    disabled={true}
                  />
                </Grid>
              ):(
                <Grid item xs={6}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.POINT_OF_SALE.FIELD}
                  label={formatMessage({ id: "operation.fields.PointOfSale" })}
                  defaultValue={currentOperation?.pointOfSaleId}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={pointsOfSaleData?.map((pointOfSale) => ({ value: pointOfSale.id, label: pointOfSale.name }))}
                  disabled={!selectedCompany || isLoadingPointsOfSale || user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              )}
              
              <Grid item xs={6}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.STATUS.FIELD}
                  label={formatMessage({ id: "operation.fields.Status" })}
                  defaultValue={currentOperation?.status}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={[
                    { label: formatMessage({id:"operation.status.PENDING"}), value: "PENDING" },
                    { label: formatMessage({id:"operation.status.COMPLETED"}), value: "COMPLETED" },
                    { label: formatMessage({id:"operation.status.ABORTED"}), value: "ABORTED" },
                  ]}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.ID_NEO_CHECK.FIELD} 
                  label={formatMessage({ id: "operation.fields.idNeoCheck" })}
                  defaultValue={currentOperation?.amount}
                  type="text"
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h2" 
                sx={
                  {
                    fontWeight: 300,
                    fontSize: "25px",
                  }
                }
                >
                  {formatMessage({ id: "operation.fields.clientInformation" })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.CLIENT_NAME.FIELD}
                  label={formatMessage({ id: "operation.fields.ClientName" })}
                  defaultValue={currentOperation?.clientName}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.CLIENT_EMAIL.FIELD}
                  label={formatMessage({ id: "operation.fields.ClientEmail" })}
                  defaultValue={currentOperation?.clientEmail}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.CLIENT_BIRTH_DATE.FIELD}
                  label={formatMessage({ id: "operation.fields.clientBirthDate" })}
                  defaultValue={currentOperation?.clientBirthDate}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.DOCUMENT_EXPIRATION_DATE.FIELD}
                  label={formatMessage({ id: "operation.fields.documentExpirationDate" })}
                  defaultValue={currentOperation?.documentExpirationDate}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.DOCUMENT_TYPE.FIELD}
                  label={formatMessage({ id: "operation.fields.DocumentType" })}
                  defaultValue={currentOperation?.documentTypeId}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={documentType.map(docType => ({
                    value: docType.value,
                    label: formatMessage({ id: docType.label })
                  }))}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.DOCUMENT_NUMBER.FIELD}
                  label={formatMessage({ id: "operation.fields.DocumentNumber" })}
                  defaultValue={currentOperation?.documentNumber}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.NATIONALITY.FIELD}
                  label={formatMessage({ id: "operation.fields.Nationality" })}
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value)}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.PROVINCE.FIELD}
                  label={formatMessage({ id: "operation.fields.province" })}
                  defaultValue={currentOperation?.province}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.CLIENT_ADDRESS.FIELD}
                  label={formatMessage({ id: "operation.fields.clientAddress" })}
                  defaultValue={currentOperation?.clientAddress}
                  disabled={user?.role!==UserRoles.SUPERADMIN}
                />
              </Grid>
              <Grid item xs={6}>
                {currentOperation?.verifiedOperation ? (
                  <Alert
                    severity="success"
                    icon={<CheckCircleIcon fontSize="inherit" />}
                    sx={{ display: "flex", alignItems: "center", fontWeight: "bold", height: "100%" }}
                  >
                    {formatMessage({ id: "operation.fields.verified" })}
                  </Alert>
                ) : (
                  <Alert
                    severity="error"
                    icon={<CancelIcon fontSize="inherit" />}
                    sx={{ display: "flex", alignItems: "center", fontWeight: "bold", height: "100%" }}
                  >
                    {formatMessage({ id: "operation.fields.disrecommended" })}
                  </Alert>
                )}
              </Grid>
              {!(currentOperation?.verifiedOperation && !currentOperation?.warningOperation) && 
                (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h2" 
                      sx={
                        {
                          fontWeight: 300,
                          fontSize: "25px",
                          }
                          }
                          >
                        {formatMessage({ id: "operation.fields.extraInformation" })}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          name={FIELDS.LEGAL_TERMS.FIELD}
                          checked={currentOperation?.acceptedLegalTerms || false}
                          disabled={true}
                          />
                        <Typography>{formatMessage({id:"operation.fields.legalTerms"})}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                    <Field.Select
                      control={methods.control}
                      name={FIELDS.PROFESIONAL_ACTIVITY.FIELD}
                      label={formatMessage({ id: "operation.fields.professionalActivity" })}
                      defaultValue={currentOperation?.professionalActivity}
                      multiple={false}
                      helperText={""}
                      fieldValue="value"
                      fieldName="label"
                      variant="outlined"
                      options={profesionalActivities.map(profActivity => ({
                        value: profActivity.value,
                        label: formatMessage({ id: `profesionalActivity.${profActivity.label}` })
                        }))}
                        disabled={user?.role!==UserRoles.SUPERADMIN}
                        />
                    </Grid>
                    <Grid item xs={6}>
                      <Field.TextField
                        name={FIELDS.PUBLIC_OFFICE.FIELD}
                        label={formatMessage({ id: "operation.fields.publicOffice" })}
                        defaultValue={currentOperation?.publicOffice}
                        disabled={user?.role!==UserRoles.SUPERADMIN}
                        />
                    </Grid>
                    <Grid item xs={6}>
                      <Field.TextField
                        name={FIELDS.PURCHASE_PURPOSE.FIELD}
                        label={formatMessage({ id: "operation.fields.purchasePurpose" })}
                        value={purchasePurpose}
                        onChange={(e) => setPurchasePurpose(e.target.value)}
                        disabled={user?.role!==UserRoles.SUPERADMIN}
                        />
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          name={FIELDS.LEGAL_FUNDS.FIELD}
                          checked={currentOperation?.legalFunds || false}
                          disabled={true}
                          />
                        <Typography>{formatMessage({id:"operation.fields.legalFunds"})}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Field.TextField
                        name={FIELDS.FUNDS_ORIGIN_COUNTRY.FIELD}
                        label={formatMessage({ id: "operation.fields.fundsOriginCountry" })}
                        defaultValue={currentOperation?.fundsOriginCountry}
                        disabled={user?.role!==UserRoles.SUPERADMIN}
                        />
                    </Grid>
                  </>
                )
                }
              <Grid item xs={12}>
                <Typography variant="h2" 
                sx={
                  {
                    fontWeight: 300,
                    fontSize: "25px",
                    }
                    }
                    >
                  {formatMessage({ id: "operation.fields.documents" })}
                </Typography>
              </Grid>
              {documentType?.find((docType) => docType.value === currentOperation?.documentTypeId)?.label === "DNI" && (
                <Grid item xs={6}>
                  <Typography>{formatMessage({id:"operation.fields.documentObverse"})}</Typography>
                  <Field.Image
                    image={currentOperation?.documentObverse}
                    name={FIELDS.DOCUMENT_OBVERSE.FIELD}
                    cssImage={{
                      width: "12.5rem",
                      height: IMAGE_HEIGHT,
                      objectFit: "cover",
                      objectPosition: "center",
                      }}
                      imageLoading={false}
                      height={IMAGE_HEIGHT}
                      />
                </Grid>
                )
                }
              
              <Grid item xs={6}>
                <Typography>{formatMessage({id:"operation.fields.documentReverse"})}</Typography>
                <Field.Image
                  image={currentOperation?.documentReverse}
                  name={FIELDS.DOCUMENT_REVERSE.FIELD}
                  cssImage={{
                    width: "12.5rem",
                    height: IMAGE_HEIGHT,
                    objectFit: "cover",
                    objectPosition: "center",
                    }}
                    imageLoading={false}
                    height={IMAGE_HEIGHT}
                    />
              </Grid>
              <Grid item xs={6}>
                <Typography>{formatMessage({id:"operation.fields.signature"})}</Typography>
                <Field.Image
                  image={currentOperation?.signature}
                  name={FIELDS.SIGNATURE.FIELD}
                  cssImage={{
                    width: "12.5rem",
                    height: IMAGE_HEIGHT,
                    objectFit: "contain",
                    objectPosition: "center",
                    }}
                    imageLoading={false}
                    height={IMAGE_HEIGHT}
                    />
              </Grid>
              <Grid item xs={6}>
                <Typography>{formatMessage({id:"operation.fields.salesDocument"})}</Typography>
                <Field.Image
                  image={currentOperation?.salesDocument}
                  name={FIELDS.SALES_DOCUMENT.FIELD}
                  cssImage={{
                    width: "12.5rem",
                    height: IMAGE_HEIGHT,
                    objectFit: "cover",
                    objectPosition: "center",
                    }}
                    imageLoading={false}
                    height={IMAGE_HEIGHT}
                    />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
              {user?.role===UserRoles.SUPERADMIN && (
                <Button variant="contained" color="primary" type="submit"  startIcon={<SaveIcon />}>
                  <Typography
                    fontWeight={500}
                    fontSize={"16px"}
                    color="#FFF"
                    alignContent="center"
                  >
                    {currentOperation?formatMessage({ id: "operation.save" }):formatMessage({ id: "operation.create" })}
                  </Typography>
                </Button>
              )}
            

            </Box>
          </Box>
        </FormProvider>
      </CardContent>
    </Card>
  );
};